import { baseApi } from "@/shared/api/baseApi";
import { ENDPOINTS } from "@/shared/api/endpoints";
import type { Shop } from "@/shared/types/shop";
import { flattenCategories, type CategoryOption } from "./lib/device";

export const layoutDesignApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** Albero categorie dello shop, appiattito per le modali dei device (non array → lista vuota). */
    getShopCategories: build.query<CategoryOption[], Shop["id"]>({
      query: (id) => ENDPOINTS.shopCategories(id),
      transformResponse: (response: unknown) => flattenCategories(response),
      // Fresco a ogni montaggio come il legacy: dopo un errore non restano le categorie vecchie.
      keepUnusedDataFor: 0,
    }),
    /** Regole di raccomandazione dello shop: servono alla config del search layer (anteprima). */
    getRecommendationRules: build.query<unknown, Shop["id"]>({
      query: (id) => ENDPOINTS.shopRecommendations(id),
      transformResponse: (response: unknown) => (response as { rules?: unknown }).rules,
    }),
    /** Configurazione del builder dello shop (solo se il builder e' attivo). */
    getBuilderConfig: build.query<unknown, Shop["id"]>({
      query: (id) => ENDPOINTS.shopBuilder(id),
    }),
    /** CSS personalizzato delle card: testo grezzo; 404 = lo shop non ne ha ancora uno salvato. */
    getCustomCss: build.query<string, Shop["id"]>({
      query: (id) => ({ url: ENDPOINTS.shopCustomCss(id), responseHandler: "text" }),
      // Fresco a ogni montaggio come il legacy: niente CSS vecchio dopo un errore, niente `error` rimasto in cache.
      keepUnusedDataFor: 0,
    }),
    saveCustomCss: build.mutation<string, { id: Shop["id"]; css: string }>({
      query: ({ id, css }) => ({
        url: ENDPOINTS.shopCustomCss(id),
        method: "PUT",
        body: css,
        headers: { "Content-Type": "text/css" },
        responseHandler: "text",
      }),
    }),
    /** Label delle Dynamic Facets (`{ facets: { chiave: label } }`); attenzione: la GET usa la `key` dello shop. */
    getFacetLabels: build.query<Record<string, string>, string>({
      query: (key) => ENDPOINTS.shopFacets(key),
      transformResponse: (response: { facets?: unknown } | null) => {
        const labels = response?.facets;
        return labels && typeof labels === "object" && !Array.isArray(labels)
          ? (labels as Record<string, string>)
          : {};
      },
      providesTags: ["FacetLabels"],
      keepUnusedDataFor: 0,
    }),
    // POST e DELETE usano l'`id`; l'invalidazione rilegge le label anche dopo un errore HTTP (il `.finally` del legacy).
    uploadFacetLabels: build.mutation<unknown, { id: Shop["id"]; facets: Record<string, string> }>({
      query: ({ id, facets }) => ({ url: ENDPOINTS.shopFacets(id), method: "POST", body: { facets } }),
      invalidatesTags: ["FacetLabels"],
    }),
    deleteFacetLabels: build.mutation<unknown, Shop["id"]>({
      query: (id) => ({ url: ENDPOINTS.shopFacets(id), method: "DELETE" }),
      invalidatesTags: ["FacetLabels"],
    }),
  }),
});

export const {
  useGetShopCategoriesQuery,
  useGetCustomCssQuery,
  useSaveCustomCssMutation,
  useGetFacetLabelsQuery,
  useUploadFacetLabelsMutation,
  useDeleteFacetLabelsMutation,
} = layoutDesignApi;
